import { Play, Copy, MoreHorizontal, Pencil, Trash2, FileDown, FileSpreadsheet, FileText, FileType } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { minutesToTimeString } from '../../utils/cloneUtils';
import { useSubscriptionLimits } from '../../../hooks/useSubscriptionLimits';
import { SubscriptionLimitModal } from '../../subscription/SubscriptionLimitModal';
import { useAlert } from '../../../context/alert/AlertProvider';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

const ScheduleMenu = ({ onEdit, onDelete, schedule, translations }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedExportType, setSelectedExportType] = useState(null);
    const menuRef = useRef(null);
    const exportMenuRef = useRef(null);
    const exportButtonRef = useRef(null);
    const [showContinueModal, setShowContinueModal] = useState(false);

    const buttonRef = useRef(null);
    const navigate = useNavigate();
    const { limits } = useSubscriptionLimits();
    const { showAlert } = useAlert();
    const [dropdownPosition, setDropdownPosition] = useState('bottom');

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }

            if (isExportOpen && exportMenuRef.current && !exportMenuRef.current.contains(event.target) && !exportButtonRef.current.contains(event.target)) {
                setIsExportOpen(false);
            }
        };

        const updateDropdownPosition = () => {
            if (buttonRef.current) {
                const buttonRect = buttonRef.current.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - buttonRect.bottom;
                const menuHeight = 150;

                setDropdownPosition(spaceBelow < menuHeight ? 'top' : 'bottom')
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', updateDropdownPosition);
        window.addEventListener('resize', updateDropdownPosition);

        if (isOpen) {
            updateDropdownPosition();
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', updateDropdownPosition);
            window.removeEventListener('resize', updateDropdownPosition);
        }

    }, [isOpen, isExportOpen]);

    const handleClone = async () => {
        if (!limits?.isProUser && limits?.hasReachedRosterLimit) {
            setShowLimitModal(true);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`https://www.ia-schedule.com:4139/api/roster/saved/${schedule.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Failed to clone schedule');

            const data = await response.json();

            const scheduleData = JSON.parse(data.requestData.requestData);


            sessionStorage.setItem('cloneInfo', JSON.stringify({
                id: data.id,
                originalId: data.rosterId,
                originalName: schedule.name || data.name,
            }));

            sessionStorage.setItem('scenarioId', JSON.stringify({
                scenarioId: data.scenarioId
            }));

            const formattedEmployees = scheduleData.Employees.map(emp => ({
                name: emp.Name,
                wkl_wrk_load: emp.Wkl_Wrk_Load,
                day_WI: emp.Day_WI,
                at_roster_num: 1,
                number: emp.Number,
                team: emp.Team
            }));

            const formattedGeneral = {
                week_or_days: scheduleData.GeneralInfo.Week_or_days,
                weeks: scheduleData.GeneralInfo.Weeks,
                days: scheduleData.GeneralInfo.Days,
                cycle: scheduleData.GeneralInfo.Cycle,
                offset: scheduleData.GeneralInfo.Offset,
                GA: scheduleData.GeneralInfo.GA,
                OFFsum: scheduleData.GeneralInfo.OffSum
            };

            const formattedSchedules = scheduleData.Schedules.map(sch => ({
                code: sch.Code,
                legend: sch.Legend,
                description: sch.Description,
                startDay: sch.StartDay,
                endDay: sch.EndDay,
                startTime: minutesToTimeString(sch.StartTime),
                endTime: minutesToTimeString(sch.EndTime),
                weightTime: sch.WeightTime,
                schedule_type: sch.Schedule_Type,
                mealBreaks: sch.MealBreaks.map(mb => ({
                    enabled: mb.Enabled,
                    startTime: mb.StartTime !== null ? minutesToTimeString(mb.StartTime) : '',
                    endTime: mb.EndTime !== null ? minutesToTimeString(mb.EndTime) : ''
                })),
                shiftTypes: sch.ShiftTypes.map(st => ({
                    active: st.Active,
                    type: st.Type
                }))
            }));


            const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

            const formattedStaffRequirements = {
                max: Array(7).fill('0'),
                min: Array(7).fill('0')
            };

            scheduleData.StaffRequirements.forEach(sr => {
                const dayIndex = daysOfWeek.indexOf(sr.DayOfWeek);
                if (dayIndex !== -1) {
                    formattedStaffRequirements.max[dayIndex] = sr.MaxStaff?.toString() || '0';
                    formattedStaffRequirements.min[dayIndex] = sr.MinStaff?.toString() || '0';
                }
            });

            const formattedDistributions = {
                Monday: [],
                Tuesday: [],
                Wednesday: [],
                Thursday: [],
                Friday: [],
                Saturday: [],
                Sunday: [],
                Holidays: [],
                "Eve of Holidays": []
            };

            scheduleData.StaffDistributions.forEach(sd => {
                formattedDistributions[sd.DayOfWeek] = sd.TimeIntervals.map(ti => ({
                    startTime: ti.StartTime,
                    endTime: ti.EndTime,
                    staff: ti.StaffCount.toString()
                }));
            });

            const formattedShiftSequences = scheduleData.ShiftSequences.map(ss => ({
                shift: ss.Shift,
                hard_min: ss.Hard_Min,
                soft_min: ss.Soft_Min,
                min_penalty: ss.Min_Penalty,
                soft_max: ss.Soft_Max,
                hard_max: ss.Hard_Max,
                max_penalty: ss.Max_Penalty
            }));

            const formattedWeeklySums = scheduleData.WeeklySums.map(ws => ({
                shift: ws.Shift,
                hard_min: ws.Hard_Min,
                soft_min: ws.Soft_Min,
                min_penalty: ws.Min_Penalty,
                soft_max: ws.Soft_Max,
                hard_max: ws.Hard_Max,
                max_penalty: ws.Max_Penalty
            }));

            const formattedShiftRestrictions = {};

            if (scheduleData.EmpOnlySch && Array.isArray(scheduleData.EmpOnlySch)) {
                scheduleData.EmpOnlySch.forEach(restriction => {
                    if (Array.isArray(restriction) && restriction.length >= 2) {
                        const employeeIndex = restriction[0];
                        const shiftIndex = restriction[1];

                        const employee = formattedEmployees[employeeIndex];
                        if (employee) {
                            const employeeId = employee.number.toString();
                            const shiftCode = formattedSchedules[shiftIndex]?.code;

                            if (shiftCode) {
                                if (!formattedShiftRestrictions[employeeId]) {
                                    formattedShiftRestrictions[employeeId] = [];
                                }

                                formattedShiftRestrictions[employeeId].push({
                                    id: Date.now() + Math.random(),
                                    scheduleCode: shiftCode,
                                    allowOnly: true
                                });
                            }
                        }
                    }
                });
            }

            if (scheduleData.EmpForbidSch && Array.isArray(scheduleData.EmpForbidSch)) {
                scheduleData.EmpForbidSch.forEach(restriction => {
                    if (Array.isArray(restriction) && restriction.length >= 2) {
                        const employeeIndex = restriction[0];
                        const shiftIndex = restriction[1];

                        const employee = formattedEmployees[employeeIndex];
                        if (employee) {
                            const employeeId = employee.number.toString();
                            const shiftCode = formattedSchedules[shiftIndex]?.code;

                            if (shiftCode) {
                                if (!formattedShiftRestrictions[employeeId]) {
                                    formattedShiftRestrictions[employeeId] = [];
                                }

                                formattedShiftRestrictions[employeeId].push({
                                    id: Date.now() + Math.random(),
                                    scheduleCode: shiftCode,
                                    allowOnly: false
                                });
                            }
                        }
                    }
                });
            }

            const formattedAbsences = {};

            if (scheduleData.AbsenceEntries && Array.isArray(scheduleData.AbsenceEntries)) {
                scheduleData.AbsenceEntries.forEach(absence => {
                    if (Array.isArray(absence) && absence.length >= 3) {
                        const employeeIndex = absence[0];
                        const scheduleIndex = absence[1];
                        const day = absence[2];

                        if (!formattedAbsences[employeeIndex]) {
                            formattedAbsences[employeeIndex] = {};
                        }

                        const absenceCode = formattedSchedules[scheduleIndex]?.code;
                        if (absenceCode) {
                            formattedAbsences[employeeIndex][day] = absenceCode;
                        }
                    }
                });
            }

            if (Object.keys(formattedAbsences).length > 0) {
                const absenceEntries = [];
                Object.entries(formattedAbsences).forEach(([employeeIndex, dayTypes]) => {
                    Object.entries(dayTypes).forEach(([day, absenceCode]) => {
                        const scheduleIndex = formattedSchedules.findIndex(s => s.code === absenceCode);
                        if (scheduleIndex !== -1) {
                            absenceEntries.push([parseInt(employeeIndex), scheduleIndex, parseInt(day)]);
                        }
                    });
                });

                sessionStorage.setItem('absenceEntries', JSON.stringify(absenceEntries));
            }

            const formattedLegislation = {
                restTime: scheduleData.Legislation?.RestTime ?? 11,
                consecutiveDays: scheduleData.Legislation?.ConsecutiveDays ?? 5
            };

            const transitions = [];
            const transitions3 = [];
            const transitions4 = [];
            const transitions5 = [];
            const penalizedTransitionsShifts = [];

            if (scheduleData.PenalizedTransitions && Array.isArray(scheduleData.PenalizedTransitions)) {
                scheduleData.PenalizedTransitions.forEach(transition => {
                    if (Array.isArray(transition) && transition.length >= 2) {
                        transitions.push([...transition]);

                        const shiftIndices = transition.slice(0, -1);
                        const shiftsSequence = shiftIndices.map(idx =>
                            formattedSchedules[idx]?.code || 'Unknown'
                        );

                        penalizedTransitionsShifts.push({
                            length: 2,
                            sequence: shiftsSequence,
                            indices: [...transition]
                        });
                    }
                });
            }

            if (scheduleData.PenalizedTransitions3 && Array.isArray(scheduleData.PenalizedTransitions3)) {
                scheduleData.PenalizedTransitions3.forEach(transition => {
                    if (Array.isArray(transition) && transition.length >= 3) {
                        transitions3.push([...transition]);

                        const shiftIndices = transition.slice(0, -1);
                        const shiftsSequence = shiftIndices.map(idx =>
                            formattedSchedules[idx]?.code || 'Unknown'
                        );

                        penalizedTransitionsShifts.push({
                            length: 3,
                            sequence: shiftsSequence,
                            indices: [...transition]
                        });
                    }
                });
            }

            if (scheduleData.PenalizedTransitions4 && Array.isArray(scheduleData.PenalizedTransitions4)) {
                scheduleData.PenalizedTransitions4.forEach(transition => {
                    if (Array.isArray(transition) && transition.length >= 4) {
                        transitions4.push([...transition]);

                        const shiftIndices = transition.slice(0, -1);
                        const shiftsSequence = shiftIndices.map(idx =>
                            formattedSchedules[idx]?.code || 'Unknown'
                        );

                        penalizedTransitionsShifts.push({
                            length: 4,
                            sequence: shiftsSequence,
                            indices: [...transition]
                        });
                    }
                });
            }

            if (scheduleData.PenalizedTransitions5 && Array.isArray(scheduleData.PenalizedTransitions5)) {
                scheduleData.PenalizedTransitions5.forEach(transition => {
                    if (Array.isArray(transition) && transition.length >= 5) {
                        transitions5.push([...transition]);

                        const shiftIndices = transition.slice(0, -1);
                        const shiftsSequence = shiftIndices.map(idx =>
                            formattedSchedules[idx]?.code || 'Unknown'
                        );

                        penalizedTransitionsShifts.push({
                            length: 5,
                            sequence: shiftsSequence,
                            indices: [...transition]
                        });
                    }
                });
            }

            sessionStorage.setItem('employees', JSON.stringify(formattedEmployees));
            sessionStorage.setItem('general', JSON.stringify(formattedGeneral));
            sessionStorage.setItem('schedules', JSON.stringify(formattedSchedules));
            sessionStorage.setItem('legislation', JSON.stringify(formattedLegislation));
            sessionStorage.setItem('absences', JSON.stringify(formattedAbsences));
            sessionStorage.setItem('staffRequirements', JSON.stringify(formattedStaffRequirements));
            sessionStorage.setItem('distributions', JSON.stringify(formattedDistributions));
            sessionStorage.setItem('shiftSequenceParams', JSON.stringify(formattedShiftSequences));
            sessionStorage.setItem('weeklySumParams', JSON.stringify(formattedWeeklySums));
            sessionStorage.setItem('shiftRestrictions', JSON.stringify(formattedShiftRestrictions));
            sessionStorage.setItem('penalizedTransitions', JSON.stringify(transitions));
            sessionStorage.setItem('penalizedTransitions_3', JSON.stringify(transitions3));
            sessionStorage.setItem('penalizedTransitions_4', JSON.stringify(transitions4));
            sessionStorage.setItem('penalizedTransitions_5', JSON.stringify(transitions5));
            sessionStorage.setItem('penalizedTransitions_shifts', JSON.stringify(penalizedTransitionsShifts));

            navigate('/form/collaborators');
        } catch (error) {
            console.error('Error cloning schedule:', error);
            showAlert({
                title: translations("alert.error"),
                description: translations("alert.error.clone"),
                variant: 'destructive'
            })
        }
    }

    const exportToExcel = async () => {
        try {
            setIsLoading(true);
            const scheduleData = await fetchScheduleData();
            const data = formatScheduleData(scheduleData);
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Schedule");
            XLSX.writeFile(wb, `${schedule.name}.xlsx`);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchScheduleData = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No token found');

            const response = await fetch(`https://www.ia-schedule.com:4139/api/roster/saved/${schedule.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch schedule data');
            }

            const data = await response.json();
            return JSON.parse(data.jsonData);
        } catch (error) {
            console.error('Error fetching schedule data:', error);
            showAlert({
                title: translations("alert.error"),
                description: translations("alert.error.export"),
                variant: 'destructive'
            });
            throw error;
        }
    };

    const decimalToTimeString = (decimalTime) => {
        if (decimalTime === 0) return '00:00';
        if (decimalTime === 24) return '24:00';

        const hours = Math.floor(decimalTime);
        const minutes = Math.round((decimalTime - hours) * 60);

        if (minutes === 60) {
            return `${hours + 1}:00`;
        }

        return `${hours}:${minutes.toString().padStart(2, '0')}`;
    };

    const formatShiftCode = (shiftCode) => {
        if (!shiftCode) return '';

        let formatted = shiftCode.replace(/\(([A-Z]+)(\d+)[.,](\d+)\)\s+(\d+)[.,](\d+)-(\d+)/g,
            (match, prefix, prefixHours, prefixMins, startHours, startMins, endHours) => {
                const prefixTime = decimalToTimeString(parseFloat(`${prefixHours}.${prefixMins}`));
                const startTime = decimalToTimeString(parseFloat(`${startHours}.${startMins}`));
                return `(${prefix}${prefixTime}) ${startTime}-${endHours}:00`;
            }
        );

        formatted = formatted.replace(/\(([A-Z]+)(\d+)[.,](\d+)\)\s+(\d+)-(\d+)[.,](\d+)/g,
            (match, prefix, prefixHours, prefixMins, startHours, endHours, endMins) => {
                const prefixTime = decimalToTimeString(parseFloat(`${prefixHours}.${prefixMins}`));
                const endTime = decimalToTimeString(parseFloat(`${endHours}.${endMins}`));
                return `(${prefix}${prefixTime}) ${startHours}-${endTime}`;
            }
        );

        formatted = formatted.replace(/\(([A-Z]+)(\d+)[.,](\d+)\)/g,
            (match, prefix, hours, mins) => {
                return `(${prefix}${decimalToTimeString(parseFloat(`${hours}.${mins}`))})`;
            }
        );

        formatted = formatted.replace(/(\d+)-(\d+)[.,](\d+)/g,
            (match, start, hours, mins) => {
                return `${start}-${decimalToTimeString(parseFloat(`${hours}.${mins}`))}`;
            }
        );

        formatted = formatted.replace(/(\d+)[.,](\d+)/g,
            (match, hours, mins) => {
                return decimalToTimeString(parseFloat(`${hours}.${mins}`));
            }
        );

        return formatted;
    };

    const formatScheduleData = (scheduleData) => {
        const dayNames = [
            translations("schedules.table.monday"),
            translations("schedules.table.tuesday"),
            translations("schedules.table.wednesday"),
            translations("schedules.table.thursday"),
            translations("schedules.table.friday"),
            translations("schedules.table.saturday"),
            translations("schedules.table.sunday")
        ];

        const totalDays = scheduleData.schedule[0]?.shifts.length || 0;
        const totalWeeks = Math.ceil(totalDays / 7);

        const columns = Array.from({ length: totalDays }, (_, i) => {
            const weekNum = Math.floor(i / 7) + 1;
            const dayIndex = i % 7;
            return `${translations("schedules.export.week")}${weekNum} ${dayNames[dayIndex]}`;
        });

        return scheduleData.schedule.map(emp => {
            const row = { [translations("schedules.detail.employee")]: emp.employee };

            emp.shifts.forEach((shift, idx) => {
                if (idx < totalDays) {
                    const key = columns[idx];
                    row[key] = shift && shift.shift
                        ? `${formatShiftCode(shift.shift)}`
                        : '-';
                }
            });

            return row;
        });
    };

    const exportToCSV = async () => {
        try {
            setIsLoading(true);
            const scheduleData = await fetchScheduleData();
            const data = formatScheduleData(scheduleData);
            const csv = Papa.unparse(data);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${schedule.name}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error exporting to CSV:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const exportToPDF = async () => {
        try {
            setIsLoading(true);
            const scheduleData = await fetchScheduleData();
            const jsPDF = (await import('jspdf')).default;

            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 10;
            const headerSize = 12;
            const bodySize = 8;
            const lineHeight = 7;
            const colNameWidth = 40;
            const daysPerWeek = 7;
            const colDayWidth = (pageWidth - margin * 2 - colNameWidth) / daysPerWeek;

            const dayNames = [translations("schedules.table.monday"),
            translations("schedules.table.tuesday"),
            translations("schedules.table.wednesday"),
            translations("schedules.table.thursday"),
            translations("schedules.table.friday"),
            translations("schedules.table.saturday"),
            translations("schedules.table.sunday")];

            let yPos = margin;
            let currentPage = 1;
            const addDocHeader = () => {
                doc.setFontSize(headerSize);
                doc.text(schedule.name, margin, yPos);
                yPos += lineHeight;
                doc.setFontSize(bodySize);
            };
            addDocHeader();

            const printTableHeader = (startDay, endDay) => {
                doc.setFont(undefined, 'bold');
                let xPos = margin;
                doc.text(translations("schedules.detail.employee"), xPos, yPos);
                xPos += colNameWidth;
                for (let d = startDay; d < endDay; d++) {
                    const label = dayNames[d % daysPerWeek];
                    doc.text(label, xPos, yPos);
                    xPos += colDayWidth;
                }
                yPos += lineHeight;
                doc.setFont(undefined, 'normal');
            };

            const ensureSpace = lines => {
                if (yPos + lineHeight * lines > pageHeight - margin) {
                    doc.addPage();
                    currentPage++;
                    yPos = margin;
                    addDocHeader();
                    return true;
                }
                return false;
            };

            const totalDays = scheduleData.schedule[0].shifts.length;
            const totalWeeks = Math.ceil(totalDays / daysPerWeek);

            for (let week = 0; week < totalWeeks; week++) {
                const startDay = week * daysPerWeek;
                const endDay = Math.min(startDay + daysPerWeek, totalDays);

                if (ensureSpace(2)) {
                    printTableHeader(startDay, endDay);
                } else {
                    printTableHeader(startDay, endDay);
                }

                scheduleData.schedule.forEach(employee => {
                    if (ensureSpace(1)) {
                        printTableHeader(startDay, endDay);
                    }
                    let xPos = margin;
                    doc.text(employee.employee, xPos, yPos);
                    xPos += colNameWidth;
                    for (let d = startDay; d < endDay; d++) {
                        const shift = employee.shifts[d];
                        const txt = formatShiftCode(shift.shift);
                        doc.text(txt, xPos, yPos);
                        xPos += colDayWidth;
                    }
                    yPos += lineHeight;
                });

                yPos += lineHeight / 2;
            }

            doc.save(`${schedule.name}.pdf`);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleExport = async (format, exportFunction) => {
        if (!limits?.isProUser && format !== 'pdf') {
            setSelectedExportType(format);
            setShowLimitModal(true);
            return;
        }

        try {
            setIsLoading(true);
            await exportFunction();
        } finally {
            setIsLoading(false);
            setIsExportOpen(false);
            setIsOpen(false);
        }
    };

    const getLastTwoWeeksData = (scheduleData, requestData) => {
        try {
            // Determinar o número total de semanas no horário original
            const totalWeeks = requestData.GeneralInfo?.Weeks ||
                (requestData.GeneralInfo?.Week_or_days === 0 ? requestData.GeneralInfo?.Weeks :
                    Math.ceil((requestData.GeneralInfo?.Days || requestData.GeneralInfo?.Weeks * 7) / 7));

            console.log('Total weeks in schedule:', totalWeeks);

            // Se o horário tem 2 semanas ou menos, retornar tudo
            if (totalWeeks <= 2) {
                console.log('Schedule has 2 weeks or less, returning all data');
                return scheduleData;
            }

            // Calcular quais semanas são as últimas duas
            const lastTwoWeeksThreshold = totalWeeks - 2;
            console.log('Last two weeks threshold (weeks >):', lastTwoWeeksThreshold);

            // Filtrar apenas as últimas duas semanas para cada funcionário
            const lastTwoWeeksSchedule = {
                ...scheduleData,
                schedule: scheduleData.schedule?.map(employee => {
                    const filteredShifts = employee.shifts?.filter(shift => {
                        const shiftWeek = Math.floor(shift.day / 7) + 1; // Semana baseada no dia (começando de 1)
                        const isInLastTwoWeeks = shiftWeek > lastTwoWeeksThreshold;

                        // Log apenas para o primeiro funcionário para debug
                        if (employee.employee === scheduleData.schedule[0].employee) {
                            console.log(`Day ${shift.day}, Week ${shiftWeek}, Include: ${isInLastTwoWeeks}`);
                        }

                        return isInLastTwoWeeks;
                    }) || [];

                    console.log(`Employee ${employee.employee}: ${employee.shifts?.length || 0} total shifts, ${filteredShifts.length} in last two weeks`);

                    return {
                        ...employee,
                        shifts: filteredShifts
                    };
                }) || []
            };

            // Log resumo
            console.log('Original schedule employees:', scheduleData.schedule?.length || 0);
            console.log('Filtered schedule employees:', lastTwoWeeksSchedule.schedule?.length || 0);

            // Log exemplo das últimas duas semanas do primeiro funcionário
            if (lastTwoWeeksSchedule.schedule?.length > 0) {
                const firstEmployee = lastTwoWeeksSchedule.schedule[0];
                console.log(`First employee (${firstEmployee.employee}) last two weeks shifts:`,
                    firstEmployee.shifts?.map(s => `Day ${s.day} (Week ${Math.floor(s.day / 7) + 1}): ${s.shift}`)
                );
            }

            return lastTwoWeeksSchedule;
        } catch (error) {
            console.error('Error extracting last two weeks:', error);
            // Em caso de erro, retorna os dados originais
            return scheduleData;
        }
    };

    const handleContinueSchedule = async () => {
        try {
            setIsLoading(true);

            const token = localStorage.getItem('token');
            const response = await fetch(`https://www.ia-schedule.com:4139/api/roster/saved/${schedule.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Failed to fetch schedule data');

            const data = await response.json();
            const scheduleData = JSON.parse(data.jsonData);
            const requestData = JSON.parse(data.requestData.requestData);

            const lastTwoWeeksData = getLastTwoWeeksData(scheduleData, requestData);

            const continuePayload = {
                lastTwoWeeksSchedule: lastTwoWeeksData,
                originalScheduleId: schedule.id,
                employees: requestData.Employees || [],
                schedules: requestData.Schedules || [],
                general: requestData.GeneralInfo || {},
                staffRequirements: requestData.StaffRequirements || [],
                staffDistributions: requestData.StaffDistributions || [],
                shiftSequences: requestData.ShiftSequences || [],
                weeklySums: requestData.WeeklySums || [],
                legislation: requestData.Legislation || {},
                absenceEntries: requestData.AbsenceEntries || [],
                empOnlySch: requestData.EmpOnlySch || [],
                empForbidSch: requestData.EmpForbidSch || [],
                penalizedTransitions: requestData.PenalizedTransitions || []
            };

            console.log(continuePayload)

            const continueResponse = await fetch(`https://www.ia-schedule.com:4139/api/roster/continue/${schedule.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(continuePayload)
            });

            if (!continueResponse.ok) {
                const errorData = await continueResponse.json();
                throw new Error(errorData.message || 'Failed to continue schedule');
            }

            const result = await continueResponse.json();

            showAlert({
                title: translations("alert.success"),
                description: translations("alert.success.continueSchedule") || "Horário continuado com sucesso!",
                variant: 'success'
            });

            navigate(`/schedules/${result.rosterId}`, {
                state: {
                    scheduleData: result.pythonResult,
                    isContinuation: true,
                    originalScheduleId: schedule.id
                }
            });


        } catch (error) {
            console.error('Error continuing schedule:', error);
            showAlert({
                title: translations("alert.error"),
                description: (translations("alert.error.continueSchedule") || "Erro ao continuar horário") + ": " + error.message,
                variant: 'destructive'
            });
        } finally {
            setIsLoading(false);
            setShowContinueModal(false);
        }
    };

    return (
        <div className='relative' ref={menuRef}>
            <button
                ref={buttonRef}
                onClick={(e) => {
                    e.preventDefault();
                    setIsOpen(!isOpen);
                }}
                className='p-2 hover:bg-gray-100 rounded-full'
                aria-label='Menu'
            >
                <MoreHorizontal className='h-5 w-5 text-gray-500' />
            </button>

            {isOpen && (
                <div
                    className={`absolute ${dropdownPosition === 'top'
                        ? 'bottom-full mb-2'
                        : 'top-full mt-2'
                        } left-0 w-48 bg-white rounded-md shadow-lg z-50`}
                >
                    <div className='py-1'>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                onEdit();
                                setIsOpen(false);
                            }}
                            className='flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full'
                        >
                            <Pencil className='h-4 w-4 mr-2' />
                            {translations("schedules.editName")}
                        </button>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                if (!limits?.isProUser && limits?.hasReachedRosterLimit) {
                                    setShowLimitModal(true);
                                } else {
                                    setShowContinueModal(true);
                                }
                                setIsOpen(false);
                            }}
                            className='flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left'
                        >
                            <Play className='h-4 w-4 mr-2' />
                            {translations("schedules.continueSchedule") || "Continuar Horário"}
                        </button>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                handleClone();
                                setIsOpen(false);
                            }}
                            className='flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full'
                        >
                            <Copy className='h-4 w-4 mr-2' />
                            {translations("schedules.clone")}
                        </button>

                        <div
                            className="relative"
                            ref={exportMenuRef}
                            onMouseEnter={() => setIsExportOpen(true)}
                            onMouseLeave={() => setIsExportOpen(false)}
                        >
                            <button
                                ref={exportButtonRef}
                                className='flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full'
                            >
                                <div className="flex items-center">
                                    <FileDown className='h-4 w-4 mr-2' />
                                    {translations("schedules.export.title") || "Export"}
                                </div>
                            </button>

                            {isExportOpen && !isLoading && (
                                <div className="absolute left-full top-0 ml-1 w-48 bg-white rounded-md shadow-lg z-20">
                                    <div className="py-1">
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleExport('pdf', exportToPDF);
                                            }}
                                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                            <FileType className="w-4 h-4 mr-2" />
                                            {translations("schedules.export.pdf")}
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleExport('excel', exportToExcel);
                                            }}
                                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                                            {translations("schedules.export.excel")}
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleExport('csv', exportToCSV);
                                            }}
                                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                            <FileText className="w-4 h-4 mr-2" />
                                            {translations("schedules.export.csv")}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                onDelete();
                                setIsOpen(false);
                            }}
                            className='flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full'
                        >
                            <Trash2 className='h-4 w-4 mr-2' />
                            {translations("schedules.delete")}
                        </button>
                    </div>
                </div>
            )}

            {/* Modal de confirmação simples para continuar horário */}
            {showContinueModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4">
                            {translations("schedules.continue.title") || "Continuar Horário"}
                        </h3>
                        <p className="text-gray-600 mb-6">
                            {translations("schedules.continue.message") || "Tem certeza que deseja continuar este horário? Serão utilizadas as últimas duas semanas como base para gerar a continuação."}
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowContinueModal(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                disabled={isLoading}
                            >
                                {translations("schedules.continue.cancel") || "Cancelar"}
                            </button>
                            <button
                                onClick={handleContinueSchedule}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                disabled={isLoading}
                            >
                                {isLoading ?
                                    (translations("schedules.continue.processing") || "Processando...") :
                                    (translations("schedules.continue.confirm") || "Confirmar")
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <SubscriptionLimitModal
                isOpen={showLimitModal}
                onClose={() => setShowLimitModal(false)}
                feature="rosters"
                limit={3}
            />
        </div>
    );
}

export default ScheduleMenu;